import {useCallback, useEffect, useRef, useState} from 'react'
import {APP_VERSION} from './lib/env'
import {useToast} from './lib/toast'
import {notify} from './lib/bugsnag'

/**
 * To test locally run
 *
 *   ./bin/development.sh start:static
 *    # update the APP_VERSION to something else (should trigger a notification)
 *    vim ./build/config.json
 */

const ACKNOWLEDGEMENT_KEY = 'acknowledged-update-version'
const acknowledgeVersion = (checksum: string) =>
  localStorage.setItem(ACKNOWLEDGEMENT_KEY, checksum)

const getLastVersionAcklowledgement = () =>
  localStorage.getItem(ACKNOWLEDGEMENT_KEY)

export const useUpdatePrompt = ({
  pollInterval = 15_000,
  initialWait = 4_000
}: {
  pollInterval?: number
  initialWait?: number
}) => {
  const intervalRef = useRef<undefined | number>()
  const [latestVersion, setLatestVersion] = useState<string>(APP_VERSION)
  const firstSent = useRef<boolean>(false)

  const checkVersion = useCallback(
    () =>
      fetch(`/config.js?cachebuster=${new Date().getTime()}`, {
        cache: 'no-cache'
      })
        .then(res => res.text())
        .then(fileContents => {
          // Note: this relies on the APP_VERSION being a single line
          // e.g. APP_VERSION: 'content-hash-1',
          const newVersionHash = fileContents.match(
            /APP_VERSION[\s:'"]*['"](content-hash-\w+)['"]/
          )?.[1]

          if (!newVersionHash)
            throw new Error(
              `Invalid hash in latest config.js - should be on a single line in the form "APP_VERSION: 'content-hash-1'"`
            )

          setLatestVersion(newVersionHash)
        })
        .catch(e => notify(e)),
    []
  )

  useEffect(() => {
    if (!firstSent.current) {
      sleep(initialWait).then(() => {
        checkVersion()
        intervalRef.current = window.setInterval(checkVersion, pollInterval)
      })
    }
    return () => clearInterval(intervalRef.current)
  }, [checkVersion, initialWait, pollInterval])

  const updateAvailable = latestVersion !== APP_VERSION

  const unseenUpdateAvailable =
    updateAvailable && getLastVersionAcklowledgement() !== latestVersion

  const acknowledge = () => acknowledgeVersion(latestVersion)

  return {updateAvailable, unseenUpdateAvailable, acknowledge}
}

const sleep = (time: number) =>
  new Promise(resolve => setTimeout(resolve, time))

export const UpdatePromptToast = () => {
  const toaster = useToast()
  const {unseenUpdateAvailable, acknowledge} = useUpdatePrompt({
    initialWait: 4_000,
    pollInterval: 15_000
  })

  useEffect(() => {
    if (unseenUpdateAvailable) {
      toaster(
        'A new version of Atomic is available',
        'info',
        () => window.location.reload(),
        'Refresh page',
        43200000 // 12 hour timeout
      )
      // acknowledge straight away as toast is a popup
      acknowledge()
    }
  }, [unseenUpdateAvailable, toaster, acknowledge])

  return null
}
